/* eslint-disable @typescript-eslint/ban-ts-comment */
import { Popover } from '@oneaudi/unified-web-components';
import { StyledIconButton } from './AudiHeaderDealerContextStyles';
import React, { lazy, Suspense, useContext, useEffect } from 'react';
import styled from 'styled-components';
import type { AudiHeaderDealerSearchProps } from '../../interfaces/header-components.interfaces';
import {
  DEALER_CONTEXT_UNSUBSCRIBE,
  KEY_DEALER_DATA,
  NAVIGATION_CLASSNAME,
} from '../../utils/globals';
import DealerSearchIntro from './DealerSearchIntro';
import useToggle from '../../hooks/use-toggle';
import AudiSearchFeatureAppLoader from '../AudiHeaderSearch/AudiSearchFeatureAppLoader';
import { ConfigContext } from '../../context/ConfigContext';
import { useI18n } from '@oneaudi/i18n-context';
import { messages } from '../../i18n/messages';

const DealerSearchFeatureApp = lazy(
  () => import(/* webpackChunkName: "lazy-dealer-search" */ './DealerSearchFeatureApp'),
);

const StyledPopover = styled(Popover)`
  padding: 0 !important;
  margin: 0;
`;

const StyledPopoverContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 392px;
  border-radius: 20px;
  padding: 0;
  overflow-y: scroll;
  scrollbar-width: none;
`;

const StyledButtonContainer = styled.div`
  cursor: pointer;
`;

const DealerSearch: React.FC<AudiHeaderDealerSearchProps> = ({
  dealerSearchGeolocationEnabled = false,
  onClickDealerSearchContext,
  showDealerSearch,
  isMobileView,
}) => {
  const { dealerSearchBenefitEnabled } = useContext(ConfigContext);
  const buttonLabel = useI18n(messages.dealership_select_button);
  const buttonLabelAriaOpen = useI18n(messages.dealership_select_button_aria);
  const buttonLabelAriaClose = useI18n(messages.dealership_close_select_button_aria);
  const [toggleElement, toggle] = useToggle();
  const storageDealerData =
    typeof window !== 'undefined' && sessionStorage?.getItem(KEY_DEALER_DATA);
  const showDealerSearchFA = !dealerSearchBenefitEnabled || showDealerSearch;

  useEffect(() => {
    if (!dealerSearchBenefitEnabled) {
      if (showDealerSearch && !toggleElement) {
        const currentScrollY = window.scrollY;
        const isScrolled = currentScrollY > 1;

        if (isScrolled) {
          const handleScrollEnd = () => {
            const scrollY = window.scrollY;

            if (scrollY === 0) {
              window.removeEventListener('scroll', handleScrollEnd);
              toggle(true);
            }
          };

          window.addEventListener('scroll', handleScrollEnd);
          window.scrollTo({ top: 0, behavior: 'smooth' });
        } else {
          toggle(true);
        }
      } else if (!showDealerSearch && toggleElement) {
        toggle(false);
      }
    } else {
      // When benefit is enabled, only sync when dealer search FA becomes active
      if (showDealerSearch && !toggleElement) {
        const currentScrollY = window.scrollY;
        const isScrolled = currentScrollY > 1;

        if (isScrolled) {
          const handleScrollEnd = () => {
            const scrollY = window.scrollY;

            if (scrollY === 0) {
              window.removeEventListener('scroll', handleScrollEnd);
              toggle(true);
            }
          };

          window.addEventListener('scroll', handleScrollEnd);
          window.scrollTo({ top: 0, behavior: 'smooth' });
        } else {
          toggle(true);
        }
      }
    }
  }, [showDealerSearch, toggleElement, dealerSearchBenefitEnabled, toggle]);

  // auto load dealer search for user that is still subscribed
  if (dealerSearchGeolocationEnabled && storageDealerData !== DEALER_CONTEXT_UNSUBSCRIBE) {
    return (
      <Suspense fallback={<AudiSearchFeatureAppLoader fromFA="dealersearch" height />}>
        <DealerSearchFeatureApp
          dealerSearchGeolocationEnabled={dealerSearchGeolocationEnabled}
          showDealerSearch={showDealerSearchFA}
          visible={false}
          isMobileView={isMobileView}
        />
      </Suspense>
    );
  }

  return (
    <StyledPopover
      content={
        <StyledPopoverContainer>
          {showDealerSearchFA ? (
            <Suspense fallback={<AudiSearchFeatureAppLoader fromFA="dealersearch" height />}>
              <DealerSearchFeatureApp
                dealerSearchGeolocationEnabled={false}
                showDealerSearch={showDealerSearchFA}
                visible
                isMobileView={isMobileView}
              />
            </Suspense>
          ) : dealerSearchBenefitEnabled ? (
            <DealerSearchIntro handleSetShowDealerSearch={onClickDealerSearchContext} />
          ) : null}
        </StyledPopoverContainer>
      }
      data-testid="dealerSearch-test-id"
      id={showDealerSearchFA ? 'dealerSearch' : 'dealerSearchIntro'}
      onClose={() => {
        toggle(false);
        if (showDealerSearch) {
          onClickDealerSearchContext();
        }
      }}
      triggerElement={
        <StyledButtonContainer>
          <StyledIconButton
            id="dealerSearchTrigger"
            aria-label={toggleElement ? buttonLabelAriaClose : buttonLabelAriaOpen}
            data-custom-attribute="custom-attribute"
            data-testid="dealer-data-toggle"
            icon={toggleElement ? 'cancel' : 'poi'}
            iconPosition="leading"
            onClick={() => {
              // Check if we need to scroll to top before opening
              const shouldOpen = !toggleElement;
              const currentScrollY = window.scrollY;
              const isScrolled = currentScrollY > 1;

              if (shouldOpen && isScrolled) {
                // Scroll to top first, then open after scroll completes
                const handleScrollEnd = () => {
                  const scrollY = window.scrollY;

                  if (scrollY === 0) {
                    window.removeEventListener('scroll', handleScrollEnd);
                    toggle();
                    if (!dealerSearchBenefitEnabled || showDealerSearch) {
                      onClickDealerSearchContext();
                    }
                  }
                };

                window.addEventListener('scroll', handleScrollEnd);
                window.scrollTo({ top: 0, behavior: 'smooth' });
              } else {
                // Normal toggle (closing or already at top)
                toggle();
                if (!dealerSearchBenefitEnabled || showDealerSearch) {
                  onClickDealerSearchContext();
                }
              }
            }}
            size="md"
            title={buttonLabel}
            variant="tertiary"
            className={NAVIGATION_CLASSNAME}
            // @ts-expect-error
            selected={toggleElement}
          >
            {buttonLabel}
          </StyledIconButton>
        </StyledButtonContainer>
      }
      withTip={false}
      open={toggleElement}
      alignment="center"
      placement="bottom"
    />
  );
};

export default DealerSearch;
